import { useEffect, useRef, useState } from "react";
import { Accessibility, ArrowDown, Check, ChevronLeft, Contrast, PanelBottom, Type, X } from "lucide-react";
import { useA11y } from "../hooks/useA11y.js";
import { A11Y_FAB_HIDE_MS, A11Y_FAB_PULSE_MS } from "../utils/a11y.js";

// SOK-09: botón "Accesible" fijo abajo a la derecha y panel con los interruptores del modo.
// En la pantalla inicial está siempre a la vista; en las demás, si no se toca en 10 s se desliza
// fuera y deja una pestaña en el borde que lo vuelve a mostrar.
// Se dibuja fuera de los modales de Radix; para que un toque aquí no cierre el modal abierto
// (Radix lo cierra con un `pointerdown` en el documento) el evento no sigue subiendo.
const keepModalsOpen = {
  onPointerDown: (e) => e.stopPropagation(),
  style: { pointerEvents: 'auto' },
};

const FEATURES = [
  { key: 'contrast', label: 'Contraste alto', hint: 'Colores de máximo contraste y bordes marcados', Icon: Contrast },
  { key: 'text', label: 'Letra grande', hint: 'Agranda los textos de todas las pantallas', Icon: Type },
  { key: 'low', label: 'Pantalla baja', hint: 'Usa solo la parte baja de la pantalla', Icon: PanelBottom },
];

export function AccessibilityButton({ alwaysVisible, logActivity }) {
  const { settings, active, toggle, reset } = useA11y();
  const [open, setOpen] = useState(false);
  // 'shown' | 'pulse' (le queda la mitad del tiempo) | 'hidden' (solo se ve la pestaña).
  const [phase, setPhase] = useState('shown');
  // Sube al tocar la pestaña, para que la cuenta parta de nuevo.
  const [revealCount, setRevealCount] = useState(0);
  const firstSwitchRef = useRef(null);

  // La cuenta corre fuera de la pantalla inicial y con el panel cerrado; llegar al inicio, abrir o
  // cerrar el panel y tocar la pestaña la reinician.
  useEffect(() => {
    setPhase('shown');
    if (alwaysVisible || open) return;
    const pulseTimer = setTimeout(() => setPhase('pulse'), A11Y_FAB_PULSE_MS);
    const hideTimer = setTimeout(() => setPhase('hidden'), A11Y_FAB_HIDE_MS);
    return () => {
      clearTimeout(pulseTimer);
      clearTimeout(hideTimer);
    };
  }, [alwaysVisible, open, revealCount]);

  const hidden = phase === 'hidden';

  useEffect(() => {
    if (!open) return;
    firstSwitchRef.current?.focus();
    const onKeyDown = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const handleOpen = () => {
    // logActivity limpia la metadata en el lugar: va una copia, no el estado.
    logActivity('a11y', 'panel', 'open', true, { settings: { ...settings } });
    setOpen(true);
  };

  const handleToggle = (key) => {
    const enabled = !settings[key];
    logActivity('a11y', key, enabled ? 'enable' : 'disable', true, { settings: { ...settings, [key]: enabled } });
    toggle(key);
  };

  const handleReveal = () => {
    logActivity('a11y', 'fab', 'reveal', true, null);
    setRevealCount((count) => count + 1);
  };

  const handleTurnOff = () => {
    logActivity('a11y', 'panel', 'disable-all', true, { settings: { ...settings } });
    reset();
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        aria-label={active ? 'Modo accesible activo. Cambiar opciones' : 'Activar modo accesible'}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-hidden={hidden}
        tabIndex={hidden ? -1 : 0}
        className={`a11y-fab cursor-pointer flex flex-col items-center justify-center gap-1 rounded-[20px] border-[3px] shadow-lg ${active ? 'a11y-fab-active' : ''} ${phase === 'pulse' ? 'a11y-fab-pulse' : ''} ${hidden ? 'a11y-fab-hidden' : ''}`}
        {...keepModalsOpen}
      >
        <Accessibility aria-hidden="true" className="w-9 h-9" strokeWidth={2.5} />
        <span className="secondary-font-bold leading-none" style={{ fontSize: '15px' }}>Accesible</span>
      </button>

      <button
        type="button"
        onClick={handleReveal}
        aria-label="Mostrar botón de accesibilidad"
        aria-hidden={!hidden}
        tabIndex={hidden ? 0 : -1}
        className={`a11y-fab-tab cursor-pointer flex items-center justify-center rounded-l-[20px] border-[3px] border-r-0 shadow-lg ${active ? 'a11y-fab-active' : ''} ${hidden ? 'a11y-fab-tab-shown' : ''}`}
        {...keepModalsOpen}
      >
        <Accessibility aria-hidden="true" className="absolute top-1.5 left-1.5 w-5 h-5" strokeWidth={2.5} />
        <ChevronLeft aria-hidden="true" className="w-8 h-8 mt-3" strokeWidth={3} />
      </button>

      {open ? (
        <div className="a11y-panel-overlay" onClick={() => setOpen(false)} {...keepModalsOpen}>
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="a11y-panel-title"
            className="a11y-panel bg-back"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-4 mb-6">
              <h2 id="a11y-panel-title" className="secondary-font-bold flex items-center gap-3 fs-34 lh-40">
                <Accessibility aria-hidden="true" className="w-10 h-10 shrink-0" strokeWidth={2.5} />
                Modo accesible
              </h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Cerrar"
                className="cursor-pointer flex items-center justify-center w-16 h-16 rounded-full border-2 shrink-0"
              >
                <X aria-hidden="true" className="w-9 h-9" strokeWidth={2.5} />
              </button>
            </div>

            <div className="flex flex-col gap-4">
              {FEATURES.map(({ key, label, hint, Icon }, index) => (
                <button
                  key={key}
                  ref={index === 0 ? firstSwitchRef : undefined}
                  type="button"
                  role="switch"
                  aria-checked={settings[key]}
                  aria-label={label}
                  aria-describedby={`a11y-hint-${key}`}
                  onClick={() => handleToggle(key)}
                  className={`a11y-switch cursor-pointer w-full flex items-center gap-5 text-left rounded-[20px] border-[3px] px-6 py-4 ${settings[key] ? 'a11y-switch-on' : ''}`}
                >
                  <Icon aria-hidden="true" className="w-10 h-10 shrink-0" strokeWidth={2.25} />
                  <span className="flex-1 min-w-0">
                    <span className="block secondary-font-bold fs-28 lh-34">{label}</span>
                    <span id={`a11y-hint-${key}`} className="block secondary-font-regular fs-20 lh-26 a11y-hint">{hint}</span>
                  </span>
                  <span aria-hidden="true" className="a11y-switch-track shrink-0">
                    <span className="a11y-switch-thumb">{settings[key] ? <Check className="w-6 h-6" strokeWidth={3} /> : null}</span>
                  </span>
                </button>
              ))}

            </div>

            <div className="flex gap-4 mt-8">
              {active ? (
                <button type="button" onClick={handleTurnOff} className="cursor-pointer flex-1 btn a11y-panel-secondary a11y-panel-action">
                  Apagar todo
                </button>
              ) : null}
              <button type="button" onClick={() => setOpen(false)} className="cursor-pointer flex-1 btn btn-primary a11y-panel-action">
                Listo
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

// Con "Pantalla baja" la app se dibuja en la franja inferior; arriba queda este aviso, sin nada
// que tocar.
export function LowZoneNotice() {
  const { settings } = useA11y();
  if (!settings.low) return null;
  return (
    <div className="a11y-low-notice bg-back" role="status">
      <Accessibility aria-hidden="true" className="w-20 h-20" strokeWidth={2} />
      <p className="secondary-font-bold text-center fs-40 lh-48">Modo accesible</p>
      <p className="secondary-font-regular text-center fs-28 lh-36 a11y-hint">Usa la parte baja de la pantalla</p>
      <ArrowDown aria-hidden="true" className="w-16 h-16 a11y-low-notice-arrow" strokeWidth={2.5} />
    </div>
  );
}
